import React, { Component, ReactNode, ErrorInfo } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { LanguageProvider } from './context/LanguageContext.tsx';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false, error: null };
  declare props: Readonly<ErrorBoundaryProps>;

  constructor(props: ErrorBoundaryProps) {
    super(props);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught application error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#FFFBEB] flex flex-col items-center justify-center p-6 text-center font-sans select-none">
          <div className="bg-white p-8 rounded-[2rem] shadow-xl max-w-md border-4 border-amber-300 space-y-4">
            <div className="text-6xl animate-bounce">🎈</div>
            <h1 className="text-2xl font-black text-slate-800">Oops! Something went wrong</h1>
            <p className="text-sm font-bold text-slate-600 leading-relaxed">
              Don't worry, little explorer! Let's restart your learning adventure.
            </p>
            <div className="pt-2 flex flex-col gap-2.5">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-black text-base rounded-2xl shadow-md border-b-4 border-emerald-700 transition-all cursor-pointer active:translate-y-[1px]"
              >
                🔄 Refresh Learning App
              </button>
              <button
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-xs rounded-xl transition-all cursor-pointer"
              >
                Clear Cache & Restart Fresh
              </button>
            </div>
            {this.state.error && (
              <details className="text-left text-[11px] font-mono bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-rose-600 max-h-36 overflow-auto">
                <summary className="cursor-pointer font-bold text-slate-700 mb-1">Error Details</summary>
                {this.state.error.toString()}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <LanguageProvider>
        <App />
      </LanguageProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

